import { ChangeEvent, useState } from 'react';
import OnboardOrganisation from '../../services/adapters/onboardOrganisation';
import { buildOnboardingPayload, OrganisationOnboardingFormState } from '../../utils/onboardingValidation';
import AdvancedInput from '../form-inputs/advancedInput';
import SsoConfigurationFields from './ssoConfigurationFields';

const InitialFormState: OrganisationOnboardingFormState = {
  samlProviderName: '',
  displayName: '',
  calculatorOrgId: '',
  emailDomains: '',
  liftDataIds: '',
  isCampaignEnabled: false,
  samlSource: 'none',
  metadataUrl: '',
  xmlMetadata: '',
  customClaimClients: '',
};

export default function OnboardingPanel() {
  const [form, setForm] = useState<OrganisationOnboardingFormState>(InitialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleField = <K extends keyof OrganisationOnboardingFormState>(key: K) => (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { target } = event;
    const value = target instanceof HTMLInputElement && target.type === 'checkbox' ? target.checked : target.value;
    setForm((prev) => ({ ...prev, [key]: value as OrganisationOnboardingFormState[K] }));
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccessMessage(null);

    const result = buildOnboardingPayload(form);
    if (!result.ok) {
      setError(result.error);
      return;
    }

    try {
      setSubmitting(true);
      const response = await new OnboardOrganisation().adaptResponse(result.payload);
      setSuccessMessage(`Onboarded ${response.displayName} (${response.samlProviderName}).`);
      setForm(InitialFormState);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to onboard organisation.');
    } finally {
      setSubmitting(false);
    }
  };

  const fieldId = (name: keyof OrganisationOnboardingFormState) => `onboarding-${name}`;

  return (
    <div className="border border-pipgrey-light rounded-md p-4 flex flex-col gap-4 max-w-3xl">
      {error && <p className="text-pipstatus-error text-sm">{error}</p>}
      {successMessage && <p className="text-pipstatus-success text-sm">{successMessage}</p>}

      <fieldset className="flex flex-col gap-2 border border-pipgrey-light rounded-md p-3">
        <legend className="px-1 text-sm font-semibold text-piptext-default">Organisation details</legend>

        <AdvancedInput
          type="text"
          id={fieldId('displayName')}
          label="Display name"
          tooltip="The display name of the organisation."
          value={form.displayName}
          onChange={handleField('displayName')}
        />

        <AdvancedInput
          type="text"
          id={fieldId('calculatorOrgId')}
          label="Calculator org ID"
          tooltip="The ID of the calculator organisation."
          value={form.calculatorOrgId}
          onChange={handleField('calculatorOrgId')}
        />

        <AdvancedInput
          type="text"
          id={fieldId('emailDomains')}
          label="Email domains"
          tooltip="Comma-separated list of email domains."
          value={form.emailDomains}
          onChange={handleField('emailDomains')}
        />

        {/* TODO(LIF-4954): replace with a multi-select fetched from a future
          GET /api/admin/lift-data endpoint (backed by LiftData
          via OrganisationLiftDataLink). */}
        <AdvancedInput
          type="text"
          id={fieldId('liftDataIds')}
          label="Lift data IDs"
          tooltip="Comma-separated list of lift data IDs."
          value={form.liftDataIds}
          onChange={handleField('liftDataIds')}
          placeholder="2, 5, 9"
          inputMode="numeric"
        />

        <AdvancedInput
          type="checkbox"
          id={fieldId('isCampaignEnabled')}
          label="Campaign enabled"
          tooltip="Select this box if the Organisation has purchased Campaign Manager."
          checked={form.isCampaignEnabled}
          onChange={handleField('isCampaignEnabled')}
        />
      </fieldset>

      <fieldset className="flex flex-col gap-2 border border-pipgrey-light rounded-md p-3">
        <legend className="px-1 text-sm font-semibold text-piptext-default">SSO Configuration</legend>
        <SsoConfigurationFields
          idPrefix="onboarding"
          value={form}
          onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
        />
      </fieldset>

      <div>
        <button
          type="button"
          className="bg-primary-dark text-white rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-60"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Onboarding…' : 'Onboard organisation'}
        </button>
      </div>
    </div>
  );
}
